import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { W as WheelGesturesPlugin } from './embla-carousel-wheel-gestures.esm-BxgUSOtT.js';
import { a as addPrevNextButtonClickHandlers } from './arrow-buttons-CGj8dylI.js';
import { t as toggleBooleanAttribute } from './toggleBooleanAttribute-Cp5m6OeZ.js';

class ProductReviewSlider extends HTMLElement {
  /** @type {import('embla-carousel').EmblaCarouselType | undefined} */
  #embla;
  /** @type {import('embla-carousel').EmblaCarouselOptions} */
  #options = {
    align: 'start',
    dragFree: true,
    slidesToScroll: 'auto',
  };
  #ratingData = this.dataset.ratingData;
  #maxReviews = Number(this.dataset.maxReviews);
  #minRating = Number(this.dataset.minRating);

  connectedCallback() {
    if (this.#ratingData) {
      const data = this.#getRelevantData();
      if (data) this.#renderReviewCards(data.reviews);
    }

    const viewport = this.querySelector('.embla__viewport');
    if (!viewport) return;

    this.#embla = this.#initEmbla(viewport);
    this.#initNavigation();
  }

  disconnectedCallback() {
    this.#embla?.destroy();
    this.#embla = undefined;
  }

  /**
   * Parses the rating data attribute
   * @returns {{reviews: object[]} | null}
   */
  #getRelevantData() {
    try {
      const ratingData = JSON.parse(this.#ratingData);
      const reviews = ratingData.reviews;
      return { reviews };
    } catch (error) {
      console.error('Error parsing rating data:', error);
      return null;
    }
  }

  /**
   * Builds a review card for each review and appends it to the embla container
   * @param {object[]} reviews - The reviews to render
   * @returns {void}
   */
  #renderReviewCards(reviews) {
    const template = this.querySelector('template[data-review-card-template]');
    const container = this.querySelector('.embla__container');
    if (!template || !container) return;

    const filteredReviews = reviews
      .filter((review) => review.rating >= this.#minRating)
      .slice(0, this.#maxReviews);

    if (filteredReviews.length === 0 && !window.Shopify?.designMode) {
      this.closest('section')?.remove();
      return;
    }
    filteredReviews.forEach((review) => {
      container.appendChild(this.#createReviewCard(template, review));
    });
  }

  /**
   * Creates a single review card from the template, filled with the given review's data
   * @param {HTMLTemplateElement} template - The review card template
   * @param {object} review - The review to fill the card with
   * @returns {Node}
   */
  #createReviewCard(template, review) {
    const card = template.content.firstElementChild.cloneNode(true);

    const rating = review.rating ?? 1;
    const body = review.body;
    const picturesUrls = review.pictures_urls ?? [];
    const author = review.reviewer_name;
    const location = review.location;
    const authorString = `${author ? author : ''}${location ? `, ${location}` : ''}`;

    const starsLabel = card.querySelector('[data-rating-stars-label]');
    if (starsLabel && rating)
      starsLabel.textContent = starsLabel.textContent.replace('[rating]', rating);

    const text = card.querySelector('[data-review-text]');
    if (text) {
      text.textContent = body ?? '';
      text.classList.toggle('hidden', !body);
    }

    const authorInfo = card.querySelector('[data-review-author-info]');
    if (authorInfo) {
      authorInfo.textContent = authorString;
      authorInfo.classList.toggle('hidden', !authorString);
    }

    const stars = card.querySelector('[data-rating-stars]');
    if (stars) this.#updateStars(stars, rating);

    const imagesContainer = card.querySelector('[data-review-images]');
    if (imagesContainer) this.#updateImages(imagesContainer, picturesUrls, author);

    const verifiedBuyerBadge = card.querySelector('[data-verified-buyer-badge]');
    if (verifiedBuyerBadge)
      this.#updateVerifiedBuyerBadge(verifiedBuyerBadge, review.verified_buyer);

    return card;
  }

  /**
   * Mutes the star icons beyond the review's rating
   * @param {HTMLElement} stars - The stars wrapper
   * @param {number} rating - The review's rating
   * @returns {void}
   */
  #updateStars(stars, rating) {
    stars.querySelectorAll('[data-star-index]').forEach((star) => {
      const isFilled = Number(star.dataset.starIndex) < rating;
      star.classList.toggle('text-gray-300', !isFilled);
    });
  }

  /**
   * Fills in up to 3 review photo thumbnails, hiding any unused slots
   * @param {HTMLElement} imagesContainer - The images wrapper
   * @param {object[]} pictures - The review's pictures_urls entries
   * @param {string} author - The review's author name
   * @returns {void}
   */
  #updateImages(imagesContainer, pictures, author) {
    imagesContainer.classList.toggle('hidden', pictures.length === 0);
    imagesContainer.querySelectorAll('img').forEach((img, index) => {
      const picture = pictures[index];
      img.classList.toggle('hidden', !picture);
      img.alt = `Review image ${index + 1}/${pictures.length} by ${author}`;
      if (picture) img.src = picture.small;
    });
  }

  /**
   * Shows or hides the verified buyer badge, hiding it from screen readers too when not shown
   * @param {HTMLElement} badge - The verified buyer badge
   * @param {boolean} isVerified - Whether the review is from a verified buyer
   * @returns {void}
   */
  #updateVerifiedBuyerBadge(badge, isVerified) {
    badge.classList.toggle('hidden', !isVerified);
    toggleBooleanAttribute(badge, 'aria-hidden', isVerified ? 'false' : 'true');
  }

  /**
   * Initializes the Embla carousel
   * @param {HTMLElement} viewport
   * @returns {import('embla-carousel').EmblaCarouselType}
   */
  #initEmbla(viewport) {
    return EmblaCarousel(viewport, this.#options, [WheelGesturesPlugin()]);
  }

  /**
   * Whether the carousel has overflow content to scroll
   * @returns {boolean}
   */
  #canScroll() {
    if (!this.#embla) return false;
    if (this.#embla.canGoToPrev() || this.#embla.canGoToNext()) return true;

    const viewport = this.#embla.rootNode();
    const container = this.#embla.containerNode();
    return container.scrollWidth > viewport.clientWidth + 1;
  }

  /**
   * Initializes the navigation buttons
   * @returns {void}
   */
  #initNavigation() {
    const nav =
      this.querySelector('[data-card-slider-nav]') ??
      this.closest('section')?.querySelector('[data-card-slider-nav]');
    const prevBtn = nav?.querySelector('[data-embla-button="prev"]');
    const nextBtn = nav?.querySelector('[data-embla-button="next"]');

    if (prevBtn && nextBtn) addPrevNextButtonClickHandlers(this.#embla, prevBtn, nextBtn);

    if (!nav) return;

    const toggleNavVisibility = () => {
      toggleBooleanAttribute(nav, 'aria-hidden', this.#canScroll() ? 'false' : 'true');
    };

    toggleNavVisibility();
    this.#embla.on('reinit', toggleNavVisibility).on('select', toggleNavVisibility);
  }
}

if (!customElements.get('product-reviews-slider')) {
  customElements.define('product-reviews-slider', ProductReviewSlider);
}
